<template>
	<q-dialog
		v-if="dialog"
		v-model="dialog"
		class="base-modal-dialog"
		:maximized="isMobileFullscreen"
		:allow-focus-outside="true"
		:persistent="props.persistent"
		:transition-show="props.transitionShow"
		:transition-hide="props.transitionHide"
		:transition-duration="props.transitionDuration"
		@hide="onClose"
	>
		<div
			:class="['base-modal', { 'base-modal--fullscreen': isMobileFullscreen }]"
			:style="computedStyles"
		>
			<q-bar
				class="base-modal__bar"
				:class="{ 'base-modal__bar--stacked-header-right': isMobileFullscreen && props.stackHeaderRightOnMobile }"
			>
				<div class="base-modal__bar-title">{{ resolvedTitle }}</div>
				<q-space />
				<div
					v-if="$slots['header-right']"
					class="base-modal__bar-right"
				>
					<slot name="header-right" />
				</div>
				<app-icon
					class="base-modal__close-btn"
					name="close"
					width="20px"
					color="#979797"
					hover-color="#499259"
					cursor="pointer"
					v-close-popup
					@click="onClose"
				/>
			</q-bar>
			<div class="base-modal__body">
				<app-loader
					v-if="loading"
					:style="loaderStyle"
				/>
				<slot />
			</div>
		</div>
	</q-dialog>
</template>

<script setup lang="ts">
import { i18n } from "@/i18n";
import {
	computed,
	ref
} from "vue";
import AppLoader from "@/components/common/AppLoader.vue";
import AppIcon from "@/components/ui-kit/icon/AppIcon.vue";
import { useBreakpoints } from "@/composables/useBreakpoints";

export type QuasarTransitions =
	"slide-right"
	| "slide-left"
	| "slide-up"
	| "slide-down"
	| "fade"
	| "scale"
	| "rotate"
	| "flip-right"
	| "flip-left"
	| "flip-up"
	| "flip-down"
	| "jump-right"
	| "jump-left"
	| "jump-up"
	| "jump-down"

type ModalProps = {
	loading?: boolean,
	persistent?: boolean,
	transitionShow?: QuasarTransitions,
	transitionHide?: QuasarTransitions,
	transitionDuration?: number,
	width?: number | string,
	maxWidth?: number | string,
	height?: number | string,
	title?: string,
	fullscreenOnMobile?: boolean,
	stackHeaderRightOnMobile?: boolean
}

const props = withDefaults(defineProps<ModalProps>(), {
	loading: false,
	persistent: true,
	transitionShow: "scale",
	transitionHide: "scale",
	transitionDuration: 300,
	width: "55vw",
	maxWidth: "55vw",
	height: "95vh",
	fullscreenOnMobile: false,
	stackHeaderRightOnMobile: false
})

const resolvedTitle = computed(() => props.title ?? String(i18n.global.t('information')))

const emit = defineEmits<{
	(e: 'on-decline'): void
	(e: 'on-accept'): void
	(e: 'on-close'): void
}>()

const dialog = ref(false)
const { isMobile } = useBreakpoints()
const isMobileFullscreen = computed(() => props.fullscreenOnMobile && isMobile.value)

const computedStyles = computed(() => {
	if (isMobileFullscreen.value) {
		return {
			width: '100vw',
			height: '100dvh',
			maxWidth: '100vw',
			maxHeight: '100dvh'
		}
	}

	// Предел ширины проставляется инлайном намеренно: у Quasar на внутреннем контейнере
	// диалога свой max-width, и перебить его из таблицы стилей нельзя.
	return {
		maxHeight: '95vh',
		maxWidth: typeof props.maxWidth === 'number'
			? `${props.maxWidth}px`
			: props.maxWidth,
		width: typeof props.width === 'number'
			? `${props.width}px`
			: props.width,
		height: typeof props.height === 'number'
			? `${props.height}px`
			: props.height,
	}
})

const loaderStyle = computed(() => ({
	'background-color': 'rgba(255, 255, 255, 0.5)'
}))

const onOpen = () => {
	dialog.value = true
}

const onClose = () => {
	emit("on-close")
	dialog.value = false
}

const onDecline = () => {
	emit("on-decline")
	dialog.value = false
}

const onAccept = () => {
	emit("on-accept")
	dialog.value = false
}

defineExpose({
	onOpen,
	onClose,
	onAccept,
	onDecline
})
</script>

<style lang="scss" scoped>
@import "@/styles/color.scss";

.base-modal {
	display: flex;
	flex-direction: column;
	width: 100%;
	background: #fff;
	border-radius: 16px;
	padding: 4px;

	&--fullscreen {
		box-sizing: border-box;
		border-radius: 0;
		padding: 0;
	}

	&__bar {
		border-bottom: 1px solid #D2D2D2;
		padding: 12px 12px 15px 12px;
		height: fit-content;
		color: #333333;
		background-color: transparent;
		box-sizing: border-box;

		&-title {
			font-size: 14px;
			line-height: 18px;
			font-weight: 600;
			color: $color-text_main;
		}

		&-right {
			display: flex;
			align-items: center;
			column-gap: 12px;
			margin-right: 12px;
			min-width: 0;
		}

		&--stacked-header-right {
			display: grid;
			grid-template-columns: minmax(0, 1fr) auto;
			grid-template-areas: "title close" "right right";
			row-gap: 8px;

			.base-modal__bar-title {
				grid-area: title;
			}

			.q-space {
				display: none;
			}

			.base-modal__bar-right {
				grid-area: right;
				width: 100%;
				margin-right: 0;
				margin-left: 0;
			}

			.base-modal__close-btn {
				grid-area: close;
				justify-self: end;
			}
		}
	}

	&__body {
		position: relative;
		flex: 1;
		width: 100%;
		overflow-x: auto;
	}
}
</style>

<style lang="scss">
.q-dialog.base-modal-dialog {
	z-index: 10001;
}
</style>
